/**
 * Figma: https://www.figma.com/design/XwMQV9iBJ1Ec4sOzWMEZcK/-Dev--Customer-Hub-2.3-%E2%80%93-Phase-1?node-id=3261-138622&m=dev
 */

import { Icon } from '@susu/ui/components'
import { useState } from 'react'

import type { Environment } from '../../../App'
import {
  getCustomMadeProductFromName,
  getCustomMadeProductUrl,
  getProductUrl,
} from '../../../config/api.config'

import {
  E_GIFTCARD_CLASS_CODE,
  PACKAGING_PRODUCT_CODE,
  PHYSICAL_GIFTCARD_CLASS_CODE,
} from '../../../constants'
import { useLabel } from '../../../i18n'
import { resolveChargeRows } from '../../../utils/charges'
import { formatPrice as formatCurrency } from '../../../utils/currency'
import type { OrderItem } from '../types'

const RETURNED_STATUS_IDS = ['18000', '8500']
const CANCELLED_STATUS_ID = '9000'

interface ProductCardProps {
  item: OrderItem
  env?: Environment
  role?: 'customer' | 'style-advisor'
  onItemDetailsClick?: (item: OrderItem) => void
  onReturnDetailsClick?: (item: OrderItem) => void
  /**
   * When provided (in-store Customer Hub), enables whole-card copy of the item
   * code for style-advisors. Confirmation is surfaced by the host toast.
   */
  onCopyProductCode?: (code: string) => void
}

const formatPrice = (price: number, currency: string): string => {
  const hasDecimals = Math.abs(price - Math.trunc(price)) > 1e-6
  return formatCurrency(price, currency, hasDecimals ? 2 : 0)
}

type ProductCardClickTarget =
  | { type: 'copyCode'; code: string }
  | { type: 'openUrl'; url: string }
  | { type: 'none' }

/**
 * Pure resolution of what clicking the card should do, kept outside the
 * component so its branching doesn't count against ProductCard's own
 * control-flow complexity (react-doctor `no-high-complexity-react-function`).
 */
function resolveProductCardClickTarget(
  item: OrderItem,
  role: 'customer' | 'style-advisor',
  env: Environment,
  canCopyProductCode: boolean,
  hasNoPdp: boolean,
): ProductCardClickTarget {
  if (canCopyProductCode) return { type: 'copyCode', code: item.productCode }
  if (role !== 'customer') return { type: 'none' }
  if (hasNoPdp) return { type: 'none' }

  if (
    item.isCustomMade &&
    item.customMadeDetails?.configurationId &&
    item.countryCode
  ) {
    // Custom made items use configurationId
    return {
      type: 'openUrl',
      url: getCustomMadeProductUrl(
        item.customMadeDetails.configurationId,
        item.countryCode,
        env,
        getCustomMadeProductFromName(item.name),
      ),
    }
  }

  if (!item.classCode) return { type: 'none' }

  // Regular items use classCode
  return { type: 'openUrl', url: getProductUrl(item.classCode, env) }
}

/**
 * Whether the item has no product page to link to — COG, packaging and both
 * gift card variants (RP-6002, RP-6111). Kept outside the component so its
 * branching doesn't count against ProductCard's own control-flow complexity
 * (react-doctor `no-high-complexity-react-function`).
 */
function resolveHasNoPdp(item: OrderItem): boolean {
  const isCOGItem = item.productCode === 'COG'
  const isPackagingItem = item.productCode === PACKAGING_PRODUCT_CODE
  const isEGiftCardItem = item.classCode === E_GIFTCARD_CLASS_CODE
  // The exact classCode is an unconfirmed guess (RP-6111) — falls back to any
  // gift card that isn't the known e-gift card so the fix still holds if the
  // guess is wrong.
  const isPhysicalGiftCardItem =
    item.classCode === PHYSICAL_GIFTCARD_CLASS_CODE ||
    (Boolean(item.giftCardNumber) && !isEGiftCardItem)

  return (
    isCOGItem || isPackagingItem || isPhysicalGiftCardItem || isEGiftCardItem
  )
}

/**
 * Visual copy affordance next to the item code on the in-store order detail
 * page. The card itself handles the copy action; this control is decorative
 * but still activatable for discoverability (nested-safe span inside the
 * card `<button>`).
 */
function CopyItemCodeButton({
  code,
  onCopy,
}: {
  code: string
  onCopy?: (code: string) => void
}) {
  const t = useLabel()

  if (!onCopy) return null

  const copy = () => onCopy(code)

  return (
    <span
      role="button"
      tabIndex={0}
      aria-label={t('orderList.item.copyItemCode', { code })}
      onClick={(e) => {
        e.stopPropagation()
        copy()
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          e.stopPropagation()
          copy()
        }
      }}
      className="flex cursor-pointer items-center"
      data-testid="copy-item-code"
    >
      <Icon name="copy" size={16} color="secondary" />
    </span>
  )
}

export default function ProductCard({
  item,
  env = 'tst',
  role = 'customer',
  onItemDetailsClick,
  onReturnDetailsClick,
  onCopyProductCode,
}: ProductCardProps) {
  const t = useLabel()
  const [imageError, setImageError] = useState(false)
  const chargeRows = resolveChargeRows(item, t('orderList.item.customizations'))
  const isCOGItem = item.productCode === 'COG'
  const isPackagingItem = item.productCode === PACKAGING_PRODUCT_CODE
  const hasNoPdp = resolveHasNoPdp(item)
  // Packaging and COG lines always carry qty 1 / price 0, which reads as a
  // pricing error rather than useful information (RP-6019).
  const showQuantityAndPrice =
    item.quantity > 0 && !isCOGItem && !isPackagingItem
  const showMultiplied = item.quantity > 1
  const discountValue = Math.abs(item.discount)
  const effectiveTotal =
    item.subTotal < 0
      ? item.subTotal + discountValue
      : item.subTotal - discountValue
  const effectiveUnitPrice =
    item.quantity > 0 ? effectiveTotal / item.quantity : item.subTotal
  const displayName = item.name?.trim() ? item.name : ''
  const isReturned =
    item.maxFulfillmentStatusId !== null &&
    RETURNED_STATUS_IDS.includes(item.maxFulfillmentStatusId)
  const isCancelled = item.maxFulfillmentStatusId === CANCELLED_STATUS_ID
  const isInProgress =
    item.maxFulfillmentStatusId === '1500' ||
    item.minFulfillmentStatusId === '1500'
  const hasOnlySecureImage =
    !item.isCustomMade &&
    Boolean(item.productDetails?.images?.[0]?.secureUrl) &&
    !item.productDetails?.images?.[0]?.fullPath
  const canCopyProductCode =
    role === 'style-advisor' &&
    Boolean(item.productCode) &&
    Boolean(onCopyProductCode)

  const handleCardClick = () => {
    const target = resolveProductCardClickTarget(
      item,
      role,
      env,
      canCopyProductCode,
      hasNoPdp,
    )

    if (target.type === 'copyCode') {
      onCopyProductCode?.(target.code)
    } else if (target.type === 'openUrl') {
      window.open(target.url, '_blank', 'noopener,noreferrer')
    }
  }

  const isClickable =
    canCopyProductCode ||
    (role === 'customer' &&
      !hasNoPdp &&
      (Boolean(item.classCode) ||
        (item.isCustomMade &&
          Boolean(item.customMadeDetails?.configurationId) &&
          Boolean(item.countryCode))))
  const hasDetailsLink =
    (!!item.customMadeDetails && !!onItemDetailsClick) || !!item.returnOrderId
  // Keep the button enabled when there's a Details link — disabled buttons block
  // pointer events on all children in Chrome, preventing the span click from firing.
  const isButtonDisabled = !isClickable && !hasDetailsLink
  const shouldShowProductCode = role === 'style-advisor' && item.productCode

  return (
    <button
      type="button"
      onClick={handleCardClick}
      disabled={isButtonDisabled}
      aria-label={
        canCopyProductCode
          ? t('orderList.item.copyProductCode', { code: item.productCode })
          : undefined
      }
      className="flex gap-16 w-full text-left hover:bg-surface-secondary transition-colors disabled:cursor-default disabled:hover:bg-transparent"
    >
      {/* Product image */}
      <div
        className={`relative w-[130px] h-[156px] flex-shrink-0 overflow-hidden rounded-sm ${
          isCOGItem || hasOnlySecureImage ? 'bg-gray-300' : 'bg-gray-300 p-16'
        }`}
      >
        {!imageError && item.image ? (
          <img
            src={item.image}
            alt={displayName}
            loading="lazy"
            decoding="async"
            onError={() => setImageError(true)}
            className="h-full w-full object-contain object-center"
          />
        ) : (
          <svg
            viewBox="0 0 40 40"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="h-full w-full"
          >
            <title>{t('orderList.item.noImage')}</title>
            <rect width="40" height="40" rx="4" fill="#E5E5E5" />
            <path d="M12 28L17 23L21 27L26 22L28 24V28H12Z" fill="#C6C6C6" />
            <circle cx="24" cy="16" r="3" fill="#C6C6C6" />
          </svg>
        )}
        {isReturned && (
          <div className="absolute top-4 right-4 flex h-20 w-20 items-center justify-center rounded-full">
            <Icon name="return-arrow" size={20} color="default" />
          </div>
        )}
      </div>

      {/* Product info */}
      <div className="flex flex-1 flex-col justify-between">
        {/* Product name and size */}
        <div className="flex flex-col gap-6">
          {isInProgress && (
            <span className="flex h-[20px] items-center px-8 pb-1 rounded-md bg-surface-alt font-sans text-xs leading-xs font-light text-text w-fit">
              {t('orderList.item.inProgress')}
            </span>
          )}
          {isReturned && !isInProgress && (
            <span className="flex h-[20px] items-center px-8 pb-1 rounded-md bg-surface-alt font-sans text-xs leading-xs font-light text-text w-fit">
              {t('orderList.item.returned')}
            </span>
          )}
          {isCancelled && !isInProgress && !isReturned && (
            <span className="flex h-[20px] items-center px-8 pb-1 rounded-md bg-surface-error font-sans text-xs leading-xs font-light text-text-error w-fit">
              {t('orderList.item.cancelled')}
            </span>
          )}
          <span className="text-base leading-base font-medium text-text">
            {displayName}
          </span>
          {shouldShowProductCode && (
            <span className="flex items-center gap-4">
              <span className="text-xs leading-xs font-light text-text-secondary">
                {item.productCode}
              </span>
              <CopyItemCodeButton
                code={item.productCode}
                onCopy={onCopyProductCode}
              />
            </span>
          )}
          {item.customMadeDetails && !item.returnOrderId ? (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation()
                onItemDetailsClick?.(item)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation()
                  onItemDetailsClick?.(item)
                }
              }}
              className="text-sm leading-sm font-light text-text underline cursor-pointer w-fit"
            >
              {t('orderList.item.details')}
            </span>
          ) : (
            <>
              {!item.customMadeDetails && item.size && (
                <span className="text-sm leading-sm font-light text-text">
                  {t('orderList.item.size', { size: item.size })}
                </span>
              )}
              {item.returnOrderId && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation()
                    onReturnDetailsClick?.(item)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.stopPropagation()
                      onReturnDetailsClick?.(item)
                    }
                  }}
                  className="text-sm leading-sm font-light text-text underline cursor-pointer w-fit"
                >
                  {t('orderList.item.returnDetails')}
                </span>
              )}
            </>
          )}
        </div>
        <div className="flex flex-col">
          {showQuantityAndPrice && (
            <div
              className="flex items-end justify-between"
              data-testid="item-quantity-price"
            >
              {/* Quantity */}
              <span className="text-sm leading-sm font-light text-text">
                {t('orderList.item.quantity', {
                  quantity: String(item.quantity),
                })}
              </span>
              <div className="flex flex-col items-end">
                {/* Multiplied Price per item */}
                {showMultiplied && (
                  <span className="text-sm leading-sm font-light text-text">
                    {item.quantity} x{' '}
                    {formatPrice(effectiveUnitPrice, item.currency)}
                  </span>
                )}
                {/* Price */}
                <div className="flex flex-shrink-0 items-start">
                  <span className="text-base leading-base font-medium text-text">
                    {formatPrice(
                      showMultiplied ? effectiveTotal : effectiveUnitPrice,
                      item.currency,
                    )}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Charges — alterations and other value added services */}
          {chargeRows.map((row) => (
            <div
              key={row.label}
              className="flex items-center justify-between"
              data-testid="item-charge"
            >
              <span className="text-sm leading-sm font-light text-text">
                {row.label}
              </span>
              <span className="text-base leading-base font-light text-text">
                {formatPrice(row.amount, item.currency)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </button>
  )
}
